import React from 'react';
import { Tour } from '@app/shared/models/tour.type';
import { useRecoilState, useSetRecoilState } from 'recoil';
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import openrouteservice from 'openrouteservice-js';
import { atom_downloadProgressBar } from '@app/shared/state/show-content-modal.atom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { getDeviceId } from '@app/shared/utils/device-id';
// revert: no per-tour index usage

export const TourDataFetcher = () => {
	const [state_progressBar, setState_progressBar] = useRecoilState(
		atom_downloadProgressBar
	);
	const { t } = useTranslation();
	return async (tour: Tour): Promise<{ sortedIndices: number[]; assets: string[] }> => {
		// Step 1: Cache the tour data API response for offline use
		const getTourUrl = `https://europe-central2-tours-app-1579553856346.cloudfunctions.net/getTour?tourId=${tour.id}`;
		try {
			const tourDataResponse = await fetch(getTourUrl);
			if (tourDataResponse.ok && 'caches' in window) {
				const apiCache = await caches.open('firebase-api-cache');
				await apiCache.put(getTourUrl, tourDataResponse.clone());
			}
		} catch (err) {
			console.warn(`[TourDataFetcher] ⚠️ Failed to cache tour data:`, err);
		}

		// Step 2: Get asset list and sorted indices using POST request
		const getAllAssUrl = `https://europe-west6-tours-app-1579553856346.cloudfunctions.net/getAllAss/`;
		const getAllAssResponse = await fetch(getAllAssUrl, {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json',
			},
			body: JSON.stringify({
				tourID: tour.id,
				action: 'both'
			})
		});
		const responseData = await getAllAssResponse.clone().json();
		const items: string[] = responseData.allAssets || [];
		const sortedIndices: number[] = responseData.sortedIndices || [];

		// Cache the asset list API response
		if ('caches' in window) {
			const apiCache = await caches.open('firebase-api-cache');
			await apiCache.put(getAllAssUrl, getAllAssResponse);
		}
		let done = 0;
		let numOfErrors = 0;

		// Smooth progress system: 0-80% for downloads, 80-100% reserved for stop cycling
		let actualProgress = 0; // Real download progress (0-80)
		let displayedProgress = 0; // What the user sees (increments smoothly by 1%)
		let progressInterval: NodeJS.Timeout | null = null;

		// Start smooth progress animation (runs in parallel with downloads)
		const startSmoothProgress = () => {
			progressInterval = setInterval(() => {
				if (displayedProgress < actualProgress && displayedProgress < 80) {
					displayedProgress = Math.min(displayedProgress + 1, actualProgress, 80);
					setState_progressBar(displayedProgress);
				}
			}, 100); // Update every 100ms for smooth 1% increments
		};

		const stopSmoothProgress = () => {
			if (progressInterval) {
				clearInterval(progressInterval);
				progressInterval = null;
			}
		};

		startSmoothProgress();

		// Use shared cache that matches the service worker configuration
		// This allows the service worker to serve cached assets when offline
		const tourCacheName = 'firebase-storage-cache';

		// Coupon pre-validation removed — validation now happens in CouponPage via CheckCoupon
		// const couponPromises = [];
		// for (const stop of tour.stops || []) {
		// 	for (const media of stop.all_media || []) {
		// 		if (media.type?.includes('coupon')) {
		// 			const uniqueID = getDeviceId();
		// 			const { uID, tID, gID } = JSON.parse(localStorage.getItem('credential_items') || '{}');
		// 			const data = {
		// 				'gID': gID,
		// 				'tID': tID,
		// 				'uID': uID,
		// 				'uniqueID': uniqueID,
		// 				'stopID': stop.id,
		// 			}
		// 			couponPromises.push(axios.post('https://europe-west3-tours-app-1579553856346.cloudfunctions.net/validateCoupon', data));
		// 		}
		// 	}
		// }
		// await Promise.allSettled(couponPromises);

		// Download asset and save to tour-specific cache - single attempt, no retries
		const downloadAsset = async (url: string): Promise<{ success: boolean, error?: string }> => {
			try {
				const response = await fetch(url, {
					mode: 'cors',
					credentials: 'omit',
					cache: 'reload', // Bypass service worker cache during download
				});
				if (!response.ok) {
					const errorMsg = `HTTP ${response.status}: ${response.statusText}`;
					return { success: false, error: errorMsg };
				}
				// Clone BEFORE reading the body (responses can only be read once)
				const responseClone = response.clone();
				const blob = await response.blob();
				// Verify blob is not empty
				if (blob.size === 0) {
					const errorMsg = 'Empty response (0 bytes)';
					return { success: false, error: errorMsg };
				}

				// Save to cache using the cloned response
				if ('caches' in window) {
					const cache = await caches.open(tourCacheName);
					await cache.put(url, responseClone);
				}

				return { success: true };
			} catch (err: any) {
				const errorMsg = err.message || 'Unknown error';
				console.error(`[TourDataFetcher] Failed:`, url, errorMsg);
				return { success: false, error: errorMsg };
			}
		};

		// Download ALL assets in parallel - maximum speed, no batching, no delays
		const downloadPromises = items.map(async (item: string, index: number) => {
			// Strip token to match what components request (see StopDetails.tsx:262, 284, 308)
			// Components always use .split('&token=')[0] when rendering media
			let assetUrl = item?.split('&token=')?.[0];
			// FIX: Force HTTPS (browser blocks HTTP from HTTPS sites)
			if (assetUrl.startsWith('http://')) {
				assetUrl = assetUrl.replace('http://', 'https://');
			}
			const result = await downloadAsset(assetUrl);
			if (!result.success) {
				numOfErrors++;
			}
			// Update ACTUAL progress (smooth animation will catch up)
			// Map downloads to 0-80% range (reserve 80-100% for stop cycling)
			actualProgress = Math.min(80, Math.round(((index + 1) / items.length) * 80));
			return result;
		});

		const results = await Promise.all(downloadPromises);

		// Stop smooth progress and set to exactly 80% (downloads complete)
		stopSmoothProgress();
		setState_progressBar(80);

		const failedAssets = results
			.filter(r => !r.success)
			.map((r, i) => `${items[i]?.split('&token=')?.[0]} (${r.error})`);

		if (failedAssets.length > 0) {
			console.warn(`[TourDataFetcher] Download completed with ${failedAssets.length} failures:`, failedAssets);
		}

		// Note: Routes are now cached lazily when the map is first viewed
		// (see NewMapContainer.tsx fetchRoutes function)

		return { sortedIndices, assets: items };
	};
};
